"use client";

import { type Dispatch, type SetStateAction, useCallback, useMemo } from "react";
import { toast } from "@/lib/use-toast";
import { toStepPayload } from "@/types/care-plan";
import type { CarePlanTemplateCycleDetail } from "@/types/care-plan-template";
import { buildCyclesPayload } from "./build-cycles-payload";
import { CarePlanContext } from "./care-plan";
import { templateCycleToDisplay } from "./template-cycle-to-display";
import { type CarePlanEditor, type CarePlanSaveState, type CarePlanStep, type DuplicatedCycleInfo, type PatientCarePlanStep } from "./types";
import { useCarePlanContextValue } from "./use-care-plan-context-value";
import { useCycleManagement } from "./use-cycle-management";
import { usePatientCarePlan } from "./use-patient-care-plan";
import { patientCarePlanSchema } from "./validation";
import { useCreateCarePlanMutation } from "@/query/patient/care-plan";
interface PatientCarePlanCreatorProps {
  patientId: string;
  steps: PatientCarePlanStep[];
  setSteps: Dispatch<SetStateAction<PatientCarePlanStep[]>>;
  startDate: Date;
  editor: CarePlanEditor;
  templateId?: string;
  templateCycles?: CarePlanTemplateCycleDetail[];
  children: React.ReactNode;
}
export type PatientCarePlanCreateInput = {
  locationId: string;
};

/**
 * Provider for creating a new patient care plan.
 *
 * Takes steps from template instantiation (the page handles template
 * fetch + instantiation + date shifting). Internalizes cycle state
 * and create mutation. The `locationId` is supplied by the caller at
 * submit time — `saveState.create({ locationId })` — so the action
 * doesn't depend on whatever location was in scope when the creator
 * first rendered.
 */
export function PatientCarePlanCreator({
  patientId,
  steps,
  setSteps,
  startDate,
  editor,
  templateId,
  templateCycles: templateCyclesProp,
  children
}: PatientCarePlanCreatorProps) {
  const createMutation = useCreateCarePlanMutation();
  const initialCycles = useMemo(() => templateCyclesProp?.map(templateCycleToDisplay) ?? [],
  // Only compute on mount — templateCyclesProp is stable from the page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  const {
    cycles,
    setCycles,
    onCycleCreated,
    renameCycle,
    duplicateCycles
  } = useCycleManagement(initialCycles, setSteps as Dispatch<SetStateAction<CarePlanStep[]>>, "");
  const handleCyclesDuplicated = useCallback((infos: DuplicatedCycleInfo[]) => {
    duplicateCycles(infos);
  }, [duplicateCycles]);
  const validation = patientCarePlanSchema.safeParse({
    name: editor.name,
    tagIds: editor.selectedTagIds,
    steps
  });
  const validationError = !validation.success ? validation.error.issues[0]?.message : undefined;
  const handleCreate = useCallback(async ({
    locationId
  }: PatientCarePlanCreateInput): Promise<boolean> => {
    if (validationError || !locationId) return false;
    try {
      const cyclesToSend = buildCyclesPayload(cycles, steps as CarePlanStep[]);
      // When creating from a template the backend instantiates its template
      // cycles first, then remaps any payload cycles whose id (or parent_cycle_id)
      // is a template cycle id to the instantiated cycle via tmpl_cycle_id_map,
      // so edits to template cycles apply to the instantiated copy instead of
      // creating duplicates.

      await createMutation.mutateAsync({
        name: editor.name.trim(),
        description: editor.description.trim() || undefined,
        patientId,
        locationId,
        startDate: startDate.toISOString(),
        templateId,
        tagIds: editor.selectedTagIds,
        steps: steps.map(toStepPayload),
        cycles: cyclesToSend
      });
      toast({
        title: "Care plan assigned",
        variant: "success"
      });
      return true;
    } catch {
      toast({
        title: "Failed to assign care plan",
        variant: "error"
      });
      return false;
    }
  }, [validationError, patientId, startDate, templateId, editor.name, editor.description, editor.selectedTagIds, steps, cycles, createMutation]);
  const saveState: CarePlanSaveState<PatientCarePlanCreateInput> = useMemo(() => ({
    create: handleCreate,
    hasChanges: true,
    isSaving: createMutation.isPending,
    errors: {},
    validationError
  }), [handleCreate, createMutation.isPending, validationError]);
  const {
    steps: currentSteps,
    selectedStepId,
    selectedStep,
    expandedStepId,
    ...actions
  } = usePatientCarePlan(steps, setSteps, startDate, {
    cycles,
    setCycles,
    onCyclesDuplicated: handleCyclesDuplicated
  });
  const value = useCarePlanContextValue({
    steps: currentSteps,
    selectedStepId,
    selectedStep,
    expandedStepId,
    actions,
    orientation: "vertical",
    detailMode: "sidebar",
    readOnly: false,
    isCreating: true,
    enableMessages: true,
    size: "md",
    editor,
    cycles,
    onCycleCreated,
    onRenameCycle: renameCycle
  });
  const valueWithSave = useMemo(() => ({
    ...value,
    saveState
  }), [value, saveState]);
  return <CarePlanContext.Provider value={valueWithSave}>
      {children}
    </CarePlanContext.Provider>;
}